import { supabase } from './supabase'
import { commentsService } from './comments'
import { curationsService } from './curations'

export interface DiscussionThread {
  discussionType: 'contribution' | 'curation' | 'founder_ask'
  targetId: string
  title: string
  slug: string | null
  imageUrl: string | null
  ownerId: string
  ownerFirstName: string
  ownerLastName: string
  ownerUsername: string | null
  ownerAvatarUrl: string | null
  lastCommentContent: string
  lastCommentUserFirstName: string
  lastCommentUserLastName: string
  lastCommentAt: string
  commentCount: number
  sharingContext: string | null
  categoryId: string | null
  categoryName: string | null
  subcategoryId: string | null
  subcategoryName: string | null
  userHasCommented: boolean
  // Founder-ask fields (optional so the many synthetic-thread constructors
  // for items/curations don't need them)
  isAnswered?: boolean
  directedTo?: string | null
}

export interface DiscussionMessage {
  id: string
  content: string
  created_at: string
  user_id: string
  attachment_type?: 'contribution' | 'curation' | null
  attachment_id?: string | null
  user?: {
    id: string
    first_name: string
    last_name: string
    username: string
    avatar_url?: string
  }
}

export interface AttachmentData {
  type: 'contribution' | 'curation'
  id: string
  title: string
  brand?: string
  imageUrl: string | null
  slug?: string
  ownerUsername?: string
  // Full contribution row for rendering with ItemCard
  contributionItem?: Record<string, any>
}

export const discussionsService = {
  /**
   * Fetch discussion threads for the authenticated user via RPC
   */
  async fetchThreads(limit = 1000): Promise<DiscussionThread[]> {
    // Two overloads of this RPC are deployed; a partial argument set is
    // ambiguous and PostgREST rejects the call (PGRST203). Always name all six.
    const { data, error } = await supabase.rpc('get_user_discussion_threads', {
      p_limit: limit,
      p_offset: 0,
      p_include_founder_asks: true,
      p_sort_by: 'recent',
      p_discussion_type: null,
      p_user_id: null
    })

    if (error) {
      console.error('Error fetching discussion threads:', error)
      throw error
    }

    const threads: DiscussionThread[] = (data || []).map((row: any) => ({
      discussionType: row.discussion_type as DiscussionThread['discussionType'],
      targetId: row.target_id,
      title: row.title,
      slug: row.slug,
      imageUrl: row.image_url,
      ownerId: row.owner_id,
      ownerFirstName: row.owner_first_name,
      ownerLastName: row.owner_last_name || '',
      ownerUsername: row.owner_username,
      ownerAvatarUrl: row.owner_avatar_url,
      lastCommentContent: row.last_comment_content,
      lastCommentUserFirstName: row.last_comment_user_first_name,
      lastCommentUserLastName: row.last_comment_user_last_name || '',
      lastCommentAt: row.last_comment_at,
      commentCount: Number(row.comment_count),
      sharingContext: row.sharing_context || null,
      categoryId: row.category_id || null,
      categoryName: row.category_name || null,
      subcategoryId: row.subcategory_id || null,
      subcategoryName: row.subcategory_name || null,
      userHasCommented: !!row.user_has_commented,
      isAnswered: !!row.is_answered,
      directedTo: row.directed_to || null
    }))

    // Enrich contribution threads with featured image
    const contributionIds = threads
      .filter(t => t.discussionType === 'contribution')
      .map(t => t.targetId)

    if (contributionIds.length > 0) {
      const { data: imageData } = await supabase
        .from('contributions')
        .select('id, images, cached_image_url')
        .in('id', contributionIds)

      // Apply featured image logic (same priority as ItemCard)
      if (imageData) {
        const imgMap = new Map<string, any>()
        for (const row of imageData as any[]) {
          imgMap.set(row.id, row)
        }
        for (const thread of threads) {
          if (thread.discussionType === 'contribution') {
            const row = imgMap.get(thread.targetId)
            if (row) {
              const images = row.images as Array<{ url?: string; cached_url?: string; highlighted?: boolean }> | null
              const highlighted = images?.find(img => img.highlighted)
              thread.imageUrl = highlighted?.cached_url || row.cached_image_url || images?.[0]?.cached_url || thread.imageUrl
            }
          }
        }
      }
    }

    return threads
  },

  /**
   * Fetch messages for a thread — delegates to existing services
   */
  async fetchMessages(type: 'contribution' | 'curation', targetId: string): Promise<DiscussionMessage[]> {
    if (type === 'contribution') {
      const comments = await commentsService.fetchComments(targetId)
      // commentsService returns newest first, we want oldest first for chat
      return [...comments].reverse()
    } else {
      return await curationsService.getCurationComments(targetId)
    }
  },

  /**
   * Post a message — delegates to existing services
   */
  async postMessage(
    type: 'contribution' | 'curation',
    targetId: string,
    userId: string,
    content: string,
    attachment?: { type: 'contribution' | 'curation'; id: string } | null
  ): Promise<DiscussionMessage> {
    if (type === 'contribution') {
      return await commentsService.createComment(targetId, userId, content, attachment)
    } else {
      return await curationsService.addCurationComment(targetId, userId, content, attachment)
    }
  },

  /**
   * Delete a message — delegates to existing services
   */
  async deleteMessage(type: 'contribution' | 'curation', commentId: string): Promise<void> {
    if (type === 'contribution') {
      await commentsService.deleteComment(commentId)
    } else {
      await curationsService.deleteCurationComment(commentId)
    }
  },

  /**
   * Subscribe to realtime updates for a thread
   */
  // Per-thread notification mutes (iMessage "Hide Alerts") across all discuss
  // types. A muted thread sends this user no pushes and no notification rows;
  // content is unaffected. Presence of the row IS the state.
  async muteThread(
    targetType: DiscussionThread['discussionType'],
    targetId: string,
    userId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('discussion_mutes')
      .upsert(
        { target_type: targetType, target_id: targetId, user_id: userId },
        { onConflict: 'target_type,target_id,user_id', ignoreDuplicates: true }
      )
    if (error) {
      console.error('Error muting thread:', error)
      throw error
    }
  },

  async unmuteThread(
    targetType: DiscussionThread['discussionType'],
    targetId: string,
    userId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('discussion_mutes')
      .delete()
      .eq('target_type', targetType)
      .eq('target_id', targetId)
      .eq('user_id', userId)
    if (error) {
      console.error('Error unmuting thread:', error)
      throw error
    }
  },

  async isThreadMuted(
    targetType: DiscussionThread['discussionType'],
    targetId: string,
    userId: string
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from('discussion_mutes')
      .select('target_id')
      .eq('target_type', targetType)
      .eq('target_id', targetId)
      .eq('user_id', userId)
      .maybeSingle()
    if (error) {
      console.error('Error reading mute state:', error)
      return false
    }
    return !!data
  },

  subscribe(
    type: 'contribution' | 'curation',
    targetId: string,
    callback: (message: DiscussionMessage) => void
  ) {
    const table = type === 'contribution' ? 'comments' : 'issue_comments'
    const filterColumn = type === 'contribution' ? 'contribution_id' : 'issue_id'

    const subscription = supabase
      .channel(`${table}:${targetId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table,
          filter: `${filterColumn}=eq.${targetId}`
        },
        async (payload) => {
          // Fetch the full comment with user data (realtime payload doesn't include joins)
          const { data } = await supabase
            .from(table)
            .select(`
              id,
              content,
              created_at,
              user_id,
              attachment_type,
              attachment_id,
              user:users!user_id(
                id,
                first_name,
                last_name,
                username,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single()

          if (data) {
            // Supabase returns joined relations as arrays; unwrap to single object
            const user = Array.isArray(data.user) ? data.user[0] : data.user
            callback({ ...data, user } as DiscussionMessage)
          }
        }
      )
      .subscribe()

    return subscription
  },

  /**
   * Batch-resolve attachment data for messages that have attachments.
   * Returns a Map keyed by attachment_id with display data.
   */
  async resolveAttachments(messages: DiscussionMessage[]): Promise<Map<string, AttachmentData>> {
    const result = new Map<string, AttachmentData>()

    const contributionIds: string[] = []
    const curationIds: string[] = []

    for (const msg of messages) {
      if (!msg.attachment_id || !msg.attachment_type) continue
      if (msg.attachment_type === 'contribution') {
        if (!contributionIds.includes(msg.attachment_id)) contributionIds.push(msg.attachment_id)
      } else {
        if (!curationIds.includes(msg.attachment_id)) curationIds.push(msg.attachment_id)
      }
    }

    // Batch-fetch contributions with full data for ItemCard rendering
    if (contributionIds.length > 0) {
      const { data } = await supabase
        .from('contributions')
        .select(`
          id, title, brand, cached_image_url, image_url, source_url, comment, created_at,
          saves_count,
          user:users!user_id(id, username, first_name, last_name, avatar_url)
        `)
        .in('id', contributionIds)

      if (data) {
        for (const c of data as any[]) {
          const user = Array.isArray(c.user) ? c.user[0] : c.user
          result.set(c.id, {
            type: 'contribution',
            id: c.id,
            title: c.title || c.brand || 'Untitled Item',
            brand: c.brand,
            imageUrl: c.cached_image_url,
            contributionItem: { ...c, user }
          })
        }
      }
    }

    // Batch-fetch curations
    if (curationIds.length > 0) {
      const { data } = await supabase
        .from('issues')
        .select(`
          id, title, slug, highlighted_image_url,
          curator:users!curator_id(username)
        `)
        .in('id', curationIds)

      if (data) {
        for (const c of data as any[]) {
          const curator = Array.isArray(c.curator) ? c.curator[0] : c.curator
          result.set(c.id, {
            type: 'curation',
            id: c.id,
            title: c.title || 'Untitled Curation',
            imageUrl: c.highlighted_image_url,
            slug: c.slug,
            ownerUsername: curator?.username
          })
        }
      }
    }

    return result
  }
}
